// What the shop does, and what this one client is here for (TW-0059).
//
// A shop is a `tattoo` shop, a `piercing` studio, or `both`; it says so on `public_data.shop_type`,
// which is chosen at signup and changed from the shop's own dashboard. THE DEFAULT IS TATTOO AND
// THAT IS LOAD-BEARING: the six shops that predate self-serve carry no `shop_type` at all, every
// row minted before today carries none, and thousands of printed QR codes point at those pages. So
// an absent, unknown or misspelt type reads as `tattoo`, and a tattoo shop's form is the form it
// has been since 2023 — same fields, same words, same posted body, down to the byte.
//
// A `both` shop asks one more question, at the top of the service block: what are you getting
// today? There is NO default answer to that one. A pre-selected "tattoo" on a piercing visit would
// produce a signed record titled Tattoo Consent and Release Form with the tattoo acknowledgements
// on it, which is exactly the document the shop keeps for the inspector — so the client picks, and
// `handleSubmit` refuses without it (the browser's own `required` is not trusted on a phone; the
// form has never relied on it, see the `isMobile` guard in Form.tsx).
//
// Everything a service changes lives here rather than in Form.tsx, so the two wordings sit side by
// side where they can be read against each other, and so the parts that are pure — which service,
// which questions, what gets posted — have tests of their own (service.test.ts).
//
// COSMETIC TATTOOING IS THE THIRD (TW-0279, docs/COSMETIC.md). `shop_type` does not change: one more
// key on the row, `public_data.cosmetic` ("also" beside what shop_type says, "only" for a cosmetic
// studio, absent for none), and `servicesFor` reads the two together. A row that has never heard of
// the key reads exactly as it did, which is the safe direction: a tattoo shop's form, its words and
// its posted body are the ones it has had since 2023 (tattooAndPiercingFormsAreUnchanged.test.tsx
// holds the whole page and the whole body). What a cosmetic signing IS, its procedures and its
// questions, is cosmetic.ts's; its words and its clauses are here, beside the other two.

import type { Lang } from "./i18n";
import jewelryMaterials from "./jewelry-materials.json";
import piercingSpots from "./piercing-spots.json";
// The Spanish consent sentences — the ONE source is consent-sentences-es.json at the repo root; this
// is its generated copy, and i18n.test.ts fails when the two drift (TW-0082).
import consentEs from "./consent-sentences-es.json";
import {
  CLAUSES_BY_PROCEDURE,
  COSMETIC_PROCEDURE_VALUES,
  COSMETIC_QUESTIONS,
  COSMETIC_STATE_CLAUSES,
  clauseShownFor,
  cosmeticLinesFor,
  cosmeticQuestionsFor,
  postedProcedures,
  type CosmeticClauseByProcedure,
} from "./cosmetic";

export type ShopType = "tattoo" | "piercing" | "both";
export type ServiceType = "tattoo" | "piercing" | "cosmetic";

export const SHOP_TYPES: ShopType[] = ["tattoo", "piercing", "both"];
export const SERVICE_TYPES: ServiceType[] = ["tattoo", "piercing", "cosmetic"];
export const DEFAULT_SHOP_TYPE: ShopType = "tattoo";

/** What this shop does. Anything that is not one of the three is a tattoo shop. */
export function shopType(value: unknown): ShopType {
  const text = `${value ?? ""}`.trim().toLowerCase();
  return (SHOP_TYPES as string[]).includes(text) ? (text as ShopType) : DEFAULT_SHOP_TYPE;
}

/** What `public_data.cosmetic` says (TW-0279): "also" (cosmetic beside whatever shop_type says), "only" (a cosmetic
 *  studio; shop_type is ignored and left alone), or "" for a shop that does no cosmetic work. Absent, empty and anything
 *  unreadable are "", the API's common.cosmetic_of read the same way: a key the form cannot read changes nothing. */
export type CosmeticOffer = "also" | "only" | "";
export function cosmeticOffer(value: unknown): CosmeticOffer {
  if (typeof value !== "string") return "";
  const text = value.trim().toLowerCase();
  return text === "also" || text === "only" ? text : "";
}

/** The services this shop's form can produce a waiver for (docs/COSMETIC.md "The shape"):
 *
 *      ["cosmetic"]                              when cosmetic is "only"
 *      the shop_type's services + ["cosmetic"]   when cosmetic is "also"
 *      the shop_type's services                  otherwise
 *
 *  where a shop_type's services are itself, and `both` is tattoo and piercing. The API's common.services_of and the shop
 *  app's lib/api.ts answer the same question the same way. */
export function servicesFor(type: ShopType, cosmetic: unknown = ""): ServiceType[] {
  const offer = cosmeticOffer(cosmetic);
  if (offer === "only") return ["cosmetic"];
  const services: ServiceType[] = type === "both" ? ["tattoo", "piercing"] : [type];
  return offer === "also" ? [...services, "cosmetic"] : services;
}

/** The same, read off the shop's own public data. */
export const servicesOf = (publicData: { shop_type?: unknown; cosmetic?: unknown } | null | undefined): ServiceType[] =>
  servicesFor(shopType(publicData?.shop_type), publicData?.cosmetic);

/** What the form starts on: the shop's one service, or nothing at all when it does more than one. A cosmetic studio's
 *  client is asked nothing: it starts on cosmetic. */
export function initialService(services: ServiceType[]): ServiceType | "" {
  return services.length === 1 ? services[0] : "";
}

/** Does this shop's client have to be asked? Exactly when its form can produce more than one kind of waiver. */
export const asksWhichService = (services: ServiceType[]): boolean => services.length > 1;

// ── the words ──────────────────────────────────────────────────────────────────────────────────

export type ServiceCopy = {
  /** The heading at the top of the page, and the name of the document being signed. */
  title: string;
  /** Why an under-18 cannot sign. 18+ for a piercing too, in this cut — see the note in
   *  docs/DECISIONS.md 2026-09-05: many states allow a minor's piercing with a parent present, and
   *  that is a per-state branch of its own once a shop asks for it. */
  ageError: string;
  section: string;
  artistLabel: string;
  artistPlaceholder: string;
  artistHint: string;
  descriptionLabel: string;
  descriptionPlaceholder: string;
  descriptionHint: string;
  locationLabel: string;
  locationHint: string;
  /** TW-0279 — a cosmetic tattoo's placement box says what goes in it (the side, the lid). And since 2026-10-26 (the form
   *  audit) the tattoo box's own, which until then was written into Form.tsx in English on both languages' pages: the English
   *  is the words it has had since 2023, so an English tattoo page is byte for byte the one it was, and a Spanish one reads
   *  Spanish in its last English box. */
  locationPlaceholder?: string;
  influenceRefusal: string;
  pregnantRefusal: string;
};

export const COPY: Record<ServiceType, ServiceCopy> = {
  tattoo: {
    title: "Tattoo Consent and Release Form",
    ageError: "You must be 18 years old to get a tattoo.",
    section: "Artist & Tattoo Information",
    artistLabel: "Name of Artist",
    artistPlaceholder: "Name of artist",
    // A hint is a line under the field, shown only when it says something the label and the
    // placeholder do not. "Name of Artist" over a box saying "Name of artist" over a line saying
    // "Enter the name of your artist" said one thing three times, on every field of this section,
    // and that stack is most of what made the form read as spread out on a phone (Chris,
    // 2026-09-07: "the vertical spacing between input elements seemed too much for modern day").
    artistHint: "",
    descriptionLabel: "Description of Tattoo",
    descriptionPlaceholder: "Tattoo description",
    descriptionHint: "",
    locationLabel: "Location on Body",
    locationPlaceholder: "Location on body",
    locationHint: "",
    influenceRefusal:
      "Sorry, we cannot proceed with the tattoo process if you're under the influence of drugs or alcohol for your safety and the quality of the artwork.",
    pregnantRefusal:
      "Sorry, we cannot proceed with the tattoo process if you're pregnant or nursing due to potential health risks to you and/or your child.",
  },
  piercing: {
    title: "Piercing Consent and Release Form",
    ageError: "You must be 18 years old to get a piercing.",
    section: "Piercer & Piercing Information",
    artistLabel: "Name of Piercer",
    artistPlaceholder: "Name of piercer",
    artistHint: "",
    descriptionLabel: "Description of Piercing",
    descriptionPlaceholder: "Piercing description",
    // the one hint that adds something: what a piercer wants in the description
    descriptionHint: "How many, and anything your piercer should know",
    locationLabel: "Piercing Location",
    locationHint: "",
    influenceRefusal:
      "Sorry, we cannot proceed with the piercing if you're under the influence of drugs or alcohol for your safety and the quality of the work.",
    pregnantRefusal:
      "Sorry, we cannot proceed with the piercing if you're pregnant or nursing due to potential health risks to you and/or your child.",
  },
  // TW-0279 — a cosmetic tattoo's own words (docs/COSMETIC.md: the title is "Cosmetic Tattoo Consent and Release Form",
  // the one the API prints). The artist is the artist, as on the signed waiver ("Name of Artist"). The description box
  // carries the shape and the colour, and says what the work is when the client picked Other; the placement box is the
  // existing `locationOnBody`, for the side (the proposal's "Where"), and optional: lips have no side. The age line is
  // the API's own refusal, word for word (app.COSMETIC_MINOR_REFUSAL): no minor signs for one, in any state.
  cosmetic: {
    title: "Cosmetic Tattoo Consent and Release Form",
    ageError: "You must be 18 years old to get a cosmetic tattoo.",
    section: "Artist & Cosmetic Tattoo Information",
    artistLabel: "Name of Artist",
    artistPlaceholder: "Name of artist",
    artistHint: "",
    descriptionLabel: "Description of Cosmetic Tattoo",
    descriptionPlaceholder: "Cosmetic tattoo description",
    descriptionHint: "The shape and the color, and what the work is if you picked Other",
    locationLabel: "Placement",
    locationHint: "",
    locationPlaceholder: "Left, right or both; upper or lower lid",
    influenceRefusal:
      "Sorry, we cannot proceed with the cosmetic tattoo if you're under the influence of drugs or alcohol, for your safety and the quality of the work.",
    pregnantRefusal:
      "Sorry, we cannot proceed with the cosmetic tattoo if you're pregnant or nursing, due to potential health risks to you and/or your child.",
  },
};

/** The words for a `both` shop before its client has picked. The form covers both and says so,
 *  rather than claiming to be one of them and changing its mind; the shared questions are the same
 *  either way, so only the sentences that name a service are neutral here. A shop that does one
 *  thing never sees any of this: `initialService` hands it its own service before the first paint. */
export const BOTH_TITLE = "Tattoo and Piercing Consent and Release Form";

export const NEUTRAL_COPY: ServiceCopy = {
  ...COPY.tattoo,
  title: BOTH_TITLE,
  ageError: "You must be 18 years old.",
  influenceRefusal:
    "Sorry, we cannot proceed if you're under the influence of drugs or alcohol, for your safety and the quality of the work.",
  pregnantRefusal:
    "Sorry, we cannot proceed if you're pregnant or nursing, due to potential health risks to you and/or your child.",
};

/** TW-0279 — and before the client of a shop whose services include cosmetic tattooing has picked: the same neutral
 *  sentences under a title that names no service, because "Tattoo and Cosmetic Tattoo …" says tattoo twice and a list of
 *  three is not a title. The record's title is the API's, from the service the client picks; this one is never printed.
 *  A `both` shop (tattoo and piercing) keeps BOTH_TITLE, the words it has had. */
export const MULTI_TITLE = "Consent and Release Form";

export const MULTI_COPY: ServiceCopy = { ...NEUTRAL_COPY, title: MULTI_TITLE };

// ── the same words in Spanish (TW-0082) ───────────────────────────────────────────────────────
// Side by side with the English on purpose, the way the tattoo and piercing wordings sit side by
// side above. Plain tattoo-counter Spanish, tú not usted; awaiting a native-speaker read.

export const COPY_ES: Record<ServiceType, ServiceCopy> = {
  tattoo: {
    title: "Formulario de consentimiento y exención para tatuaje",
    ageError: "Debes tener 18 años para hacerte un tatuaje.",
    section: "Artista y tatuaje",
    artistLabel: "Nombre del artista",
    artistPlaceholder: "Nombre del artista",
    artistHint: "",
    descriptionLabel: "Descripción del tatuaje",
    descriptionPlaceholder: "Descripción del tatuaje",
    descriptionHint: "",
    locationLabel: "Ubicación en el cuerpo",
    locationPlaceholder: "Ubicación en el cuerpo",
    locationHint: "",
    influenceRefusal:
      "Lo sentimos: por tu seguridad y la calidad del trabajo, no podemos hacer el tatuaje si estás bajo los efectos de drogas o alcohol.",
    pregnantRefusal:
      "Lo sentimos: no podemos hacer el tatuaje si estás embarazada o amamantando, por los posibles riesgos para ti y/o tu bebé.",
  },
  piercing: {
    title: "Formulario de consentimiento y exención para perforación",
    ageError: "Debes tener 18 años para hacerte una perforación.",
    section: "Perforador y perforación",
    artistLabel: "Nombre del perforador",
    artistPlaceholder: "Nombre del perforador",
    artistHint: "",
    descriptionLabel: "Descripción de la perforación",
    descriptionPlaceholder: "Descripción de la perforación",
    descriptionHint: "Cuántas, y cualquier cosa que tu perforador deba saber",
    locationLabel: "Ubicación de la perforación",
    locationHint: "",
    influenceRefusal:
      "Lo sentimos: por tu seguridad y la calidad del trabajo, no podemos hacer la perforación si estás bajo los efectos de drogas o alcohol.",
    pregnantRefusal:
      "Lo sentimos: no podemos hacer la perforación si estás embarazada o amamantando, por los posibles riesgos para ti y/o tu bebé.",
  },
  // TW-0279 — awaiting a native-speaker read, like every Spanish line on the form
  cosmetic: {
    title: "Formulario de consentimiento y exención para tatuaje cosmético",
    ageError: "Debes tener 18 años para hacerte un tatuaje cosmético.",
    section: "Artista y tatuaje cosmético",
    artistLabel: "Nombre del artista",
    artistPlaceholder: "Nombre del artista",
    artistHint: "",
    descriptionLabel: "Descripción del tatuaje cosmético",
    descriptionPlaceholder: "Descripción del tatuaje cosmético",
    descriptionHint: "La forma y el color, y qué trabajo es si elegiste Otro",
    locationLabel: "Ubicación",
    locationHint: "",
    locationPlaceholder: "Izquierda, derecha o ambos lados; párpado superior o inferior",
    influenceRefusal:
      "Lo sentimos: por tu seguridad y la calidad del trabajo, no podemos hacer el tatuaje cosmético si estás bajo los efectos de drogas o alcohol.",
    pregnantRefusal:
      "Lo sentimos: no podemos hacer el tatuaje cosmético si estás embarazada o amamantando, por los posibles riesgos para ti y/o tu bebé.",
  },
};

export const NEUTRAL_COPY_ES: ServiceCopy = {
  ...COPY_ES.tattoo,
  title: "Formulario de consentimiento y exención para tatuaje y perforación",
  ageError: "Debes tener 18 años.",
  influenceRefusal:
    "Lo sentimos: por tu seguridad y la calidad del trabajo, no podemos continuar si estás bajo los efectos de drogas o alcohol.",
  pregnantRefusal:
    "Lo sentimos: no podemos continuar si estás embarazada o amamantando, por los posibles riesgos para ti y/o tu bebé.",
};

export const MULTI_COPY_ES: ServiceCopy = { ...NEUTRAL_COPY_ES, title: "Formulario de consentimiento y exención" };

/** The words for this service in this language. English, with no language named, is the page
 *  exactly as it has been — every caller that predates the switch says nothing and gets that.
 *  Before a multi-service shop's client picks, the neutral words: a `both` shop's own, or the ones
 *  that name no service once cosmetic tattooing is among the shop's services. */
export const copyFor = (service: ServiceType | "", lang: Lang = "en", services: ServiceType[] = []): ServiceCopy => {
  if (service) return lang === "es" ? COPY_ES[service] : COPY[service];
  const multi = services.includes("cosmetic");
  return lang === "es" ? (multi ? MULTI_COPY_ES : NEUTRAL_COPY_ES) : multi ? MULTI_COPY : NEUTRAL_COPY;
};

// ── where a piercing goes ──────────────────────────────────────────────────────────────────────

/** A list rather than a free-text box for one reason: whether the piercing is in or through the
 *  mouth decides whether the form asks about recent dental work, and a typed "labret" cannot be
 *  read for that. Anything not on the list is "Somewhere else", and the description says what. */
/** `value` is what gets POSTED and what the PDF prints — English, so a piercer's records read the
 *  same whatever language the client picked; `es` is only what the Spanish list SHOWS. */
export type PiercingLocation = { value: string; es: string; oral?: true };

/** The list lives ONCE, at the repo root (piercing-spots.json; edit there, then cp to the copies beside the form, the shop app
 *  and the API, the jewelry-materials.json shape), because the shop app's Settings offers the same list to an owner ticking the
 *  piercings the shop does (TW-0422) and the API cleans that list against it. This copy is generated: piercing-spots.test.ts
 *  fails on drift. The order is the picker's. */
export const PIERCING_LOCATIONS: PiercingLocation[] = piercingSpots as PiercingLocation[];

export const locationLabel = (option: PiercingLocation, lang: Lang = "en"): string =>
  lang === "es" ? option.es : option.value;

/** TW-0422 — the section's heading on a page that does not ask who did the work (the owner's "Ask which piercer" or "Ask which
 *  artist" off, artists.ts asksArtistFor): the work alone, where every other page names the artist and the work together. The
 *  Spanish awaits a native-speaker read, like every Spanish line on the form. */
const SECTION_WITHOUT_ARTIST: Record<Lang, Record<ServiceType, string>> = {
  en: { tattoo: "Tattoo Information", piercing: "Piercing Information", cosmetic: "Cosmetic Tattoo Information" },
  es: { tattoo: "Tatuaje", piercing: "Perforación", cosmetic: "Tatuaje cosmético" },
};

export const sectionWithoutArtist = (service: ServiceType, lang: Lang = "en"): string => SECTION_WITHOUT_ARTIST[lang][service];

/** The one spot that is always on the list, whatever the shop ticked: a client can still describe a piercing the list does not
 *  name, and the description says what (TW-0422). */
export const SOMEWHERE_ELSE = "Somewhere else";

/** THE PIERCINGS THIS SHOP DOES (TW-0422, 2026-10-22). A shop that does both in Kennewick WA (biggestlittleshopoffun), through the contact
 *  form: "We also don't offer all the piercings you've listed on your form." `public_data.piercing_spots` is the owner's list, set in the
 *  shop app's Settings → Waiver form → Piercings you do (by hand, scripts/set-a-shops-piercing-spots.py): the `value`s of the
 *  spots the shop does. The picker lists those, in its own order, and "Somewhere else" always. ABSENT IS EVERY SPOT, the list
 *  every piercing form has had, and so is anything that is not a list or names no spot on it (the API never stores either: it
 *  refuses an empty list and removes the key when every spot is ticked), so a row that never said reads exactly as it did.
 *  The dental-work question follows whichever spot is picked (isOralPiercing), so it holds for whatever the shop ticked. */
export function piercingSpotsFor(publicData: unknown): PiercingLocation[] {
  const raw = (publicData as { piercing_spots?: unknown } | null | undefined)?.piercing_spots;
  if (!Array.isArray(raw)) return PIERCING_LOCATIONS;
  const ticked = new Set(raw.filter((v): v is string => typeof v === "string").map((v) => v.trim()));
  const kept = PIERCING_LOCATIONS.filter((spot) => spot.value !== SOMEWHERE_ELSE && ticked.has(spot.value));
  if (!kept.length) return PIERCING_LOCATIONS;
  return PIERCING_LOCATIONS.filter((spot) => spot.value === SOMEWHERE_ELSE || ticked.has(spot.value));
}

const ORAL = new Set(PIERCING_LOCATIONS.filter((l) => l.oral).map((l) => l.value));

/** Is this piercing in or through the mouth? Only then is recent dental work asked about. */
export const isOralPiercing = (location: string): boolean => ORAL.has(`${location ?? ""}`.trim());

/** What the jewelry is made of. Nickel is the allergy a shop hears about, so it is named. The
 *  English is what gets posted; the Spanish is only what the list shows. The list is the repo root's
 *  jewelry-materials.json, copied beside this file (the pricing.json shape: edit the root, cp the
 *  copies; jewelry-materials.test.ts fails on drift), because the shop app's Settings offers the
 *  same list when a shop fixes its material (below). */
export const JEWELRY_MATERIALS: string[] = jewelryMaterials;

/** THE SHOP'S ONE MATERIAL, when it uses only one (2026-09-14; Robert Jarrett, 2 Ton Gallery,
 *  Kingsport TN, through the contact form: "I'd like to make it where they don't choose the jewelry
 *  type because we only use titanium."). `public_data.jewelry_material` on the row, set in the shop
 *  app's Settings → Waiver form → Jewelry; "" or absent means the client is asked, which is what every
 *  piercing form has done since TW-0059. With a material fixed, the form shows it as a line rather
 *  than a select and POSTS it as `jewelryMaterial`, so the signed waiver, the record and the export
 *  read exactly what they always have: the material, under the same key. Any short label the shop
 *  set is honoured (a hand-written row can say "Implant-grade titanium"); the Spanish label exists
 *  only for the five on the list. */
export function fixedJewelryMaterial(publicData: unknown): string {
  const raw = (publicData as { jewelry_material?: unknown } | null | undefined)?.jewelry_material;
  return typeof raw === "string" ? raw.trim() : "";
}

const JEWELRY_MATERIALS_ES: Record<string, string> = {
  "Surgical steel": "Acero quirúrgico",
  Titanium: "Titanio",
  Niobium: "Niobio",
  Gold: "Oro",
  "Nickel-containing or other": "Con níquel u otro",
};

export const jewelryMaterialLabel = (value: string, lang: Lang = "en"): string =>
  lang === "es" ? JEWELRY_MATERIALS_ES[value] ?? value : value;

// ── the questions a piercing adds ──────────────────────────────────────────────────────────────

export type PiercingQuestion = {
  /** the key on form.questionnaire */
  name: "metalAllergy" | "keloidScarring" | "recentDentalWork";
  /** the free-text field that follows a yes */
  details: "metalAllergyDetails" | "keloidScarringDetails" | "recentDentalWorkDetails";
  label: string;
  detailsLabel: string;
  labelEs: string;
  detailsLabelEs: string;
  /** asked only for a piercing in or through the mouth */
  oralOnly?: true;
};

export const PIERCING_QUESTIONS: PiercingQuestion[] = [
  {
    name: "metalAllergy",
    details: "metalAllergyDetails",
    label: "Are you allergic to any metals, including nickel?",
    detailsLabel: "If yes, which metals?",
    labelEs: "¿Eres alérgico/a a algún metal, incluido el níquel?",
    detailsLabelEs: "Si es así, ¿a cuáles?",
  },
  {
    name: "keloidScarring",
    details: "keloidScarringDetails",
    label: "Have you ever had a keloid or a raised scar?",
    detailsLabel: "If yes, where, and what happened?",
    labelEs: "¿Alguna vez has tenido un queloide o una cicatriz abultada?",
    detailsLabelEs: "Si es así, ¿dónde y qué pasó?",
  },
  {
    name: "recentDentalWork",
    details: "recentDentalWorkDetails",
    label: "Have you had dental work in the last 2 weeks?",
    detailsLabel: "If yes, what was done, and when?",
    labelEs: "¿Te han hecho algún trabajo dental en las últimas 2 semanas?",
    detailsLabelEs: "Si es así, ¿qué te hicieron y cuándo?",
    oralOnly: true,
  },
];

/** The question and its follow-up, in the client's language. */
export const questionWords = (q: PiercingQuestion, lang: Lang = "en"): { label: string; detailsLabel: string } =>
  lang === "es" ? { label: q.labelEs, detailsLabel: q.detailsLabelEs } : { label: q.label, detailsLabel: q.detailsLabel };

/** The questions this visit actually asks. An earlobe is never asked about dental work, so the
 *  record never carries a line implying it was. */
export function piercingQuestionsFor(location: string): PiercingQuestion[] {
  const oral = isOralPiercing(location);
  return PIERCING_QUESTIONS.filter((q) => !q.oralOnly || oral);
}

// ── what the client agrees to ──────────────────────────────────────────────────────────────────

/** A cosmetic tattoo's own clauses (TW-0279): C1 to C14 on every cosmetic form, a state's own line in the place of the
 *  plain clause it replaces (Oregon's numbingOregon and Michigan's numbingMichigan for C9), then C15 to C20 by procedure
 *  (cosmetic.ts CLAUSES_BY_PROCEDURE). `explained` is shared with the other two, in a cosmetic tattoo's words. */
export type CosmeticAckKey =
  | "permanentPigment"
  | "darkerAtFirst"
  | "fadesAndRefreshes"
  | "resultsVary"
  | "approvedShapeAndColor"
  | "pigmentNotApproved"
  | "normalHealing"
  | "pigmentAllergy"
  | "numbingReaction"
  | "numbingOregon"
  | "numbingMichigan"
  | "answeredTruthfully"
  | "laterTreatments"
  | "mriScan"
  | "aftercareGiven"
  | "changesWithAge"
  | CosmeticClauseByProcedure;

export type AckKey =
  | "explained"
  | "outcome"
  | "restrictions"
  | "permanentChange"
  | "photograph"
  | "refund"
  | "allergic"
  | "takeCare"
  | "infection"
  | "migration"
  | "indemnify"
  | "information"
  | CosmeticAckKey;

/** The acknowledgements in the order they are shown, per service. `migration` — a body moving or
 *  rejecting a piercing — has no tattoo equivalent, so it exists only on a piercing's form and
 *  only on a piercing's record. `termsAndConditions` is not here: it carries a link, so Form.tsx
 *  builds it, and its wording does not change with the service. */
// TW-0107 (2026-09-08) — three the strict states' consent rules name and the form never said as such:
// an explanation of the procedure (`explained`), adverse outcomes (`outcome`) and restrictions while it
// heals (`restrictions`) — Oregon's OAR 331-915-0085(2)(a), (d), (e). Standard on every form (Chris,
// 2026-09-08: "i'd prefer we finish all of this so i can ship it confidently"): a shop in a state that
// never lists them loses nothing, and the state audit never has to add them.
export const ACK_ORDER: Record<ServiceType, AckKey[]> = {
  tattoo: [
    "explained",
    "permanentChange",
    "outcome",
    "photograph",
    "refund",
    "allergic",
    "takeCare",
    "restrictions",
    "infection",
    "indemnify",
    "information",
  ],
  piercing: [
    "explained",
    "permanentChange",
    "outcome",
    "photograph",
    "refund",
    "allergic",
    "takeCare",
    "restrictions",
    "infection",
    "migration",
    "indemnify",
    "information",
  ],
  // TW-0279 — A COSMETIC TATTOO'S, the API's own list in the API's own order (cosmetic.CLAUSES: `explained` in a cosmetic
  // tattoo's words, C1 to C14 with a state's own line beside the clause it replaces, then C15 to C20), which ackOrderFor
  // below narrows to what one visit shows (a state's lines, the procedures picked), then the three tattoo clauses that
  // name no service and make the "Release" the title promises: the restrictions while it heals, no refunds, the
  // indemnity (app.COSMETIC_SHARED_CLAUSES; the slice-1 handover's order). `termsAndConditions` is the terms box Form.tsx
  // builds for every service. Nothing else: a tattoo's clauses that name the tattoo (photograph, allergic, takeCare,
  // infection), the piercing's migration, and the three C1, C4 and C10 say better (permanentChange, outcome,
  // information) are never on a cosmetic form, so never on its body (the door refuses one: COSMETIC_CLAUSE_NOT_ON_THE_FORM).
  cosmetic: [
    "explained",
    "permanentPigment",
    "darkerAtFirst",
    "fadesAndRefreshes",
    "resultsVary",
    "approvedShapeAndColor",
    "pigmentNotApproved",
    "normalHealing",
    "pigmentAllergy",
    "numbingReaction",
    "numbingOregon",
    "numbingMichigan",
    "answeredTruthfully",
    "laterTreatments",
    "mriScan",
    "aftercareGiven",
    "changesWithAge",
    "lipsColdSore",
    "eyelinerNearEye",
    "areolaTreatedSkin",
    "scalpSessions",
    "camouflageMatch",
    "microbladingFades",
    "restrictions",
    "refund",
    "indemnify",
  ],
};

/** Every acknowledgement key any service uses, the UNION of every service's list: the denominator for the strip below.
 *  It was piercing's list until TW-0279, which held only while piercing's was the biggest; a cosmetic clause missing from
 *  it would ride every tattoo body as `false` (the map's third risk). */
export const ALL_ACK_KEYS: AckKey[] = Array.from(new Set<AckKey>([...ACK_ORDER.tattoo, ...ACK_ORDER.piercing, ...ACK_ORDER.cosmetic]));

/** The clauses this visit shows, in order: the service's list, and for a cosmetic tattoo the ones the SHOP's state
 *  carries (its record's `lines`, off the contract: a line's `replaces` is not shown, a state's own clause is shown only
 *  where a line names it; California has no C6, Oregon its own C9) and only the by-procedure ones (C15 to C20) a picked
 *  procedure brings. What is shown is what is ticked, gated and posted, and the door holds a cosmetic body to exactly
 *  this set (app.cosmetic_clauses_on_the_form). A tattoo's and a piercing's lists do not move with the state. */
export function ackOrderFor(service: ServiceType, procedures: readonly string[] = [], state: string | null = null): AckKey[] {
  if (service !== "cosmetic") return ACK_ORDER[service];
  const lines = cosmeticLinesFor(state);
  const replaced = new Set(lines.map((line) => line.replaces).filter(Boolean));
  const named = new Set(lines.map((line) => line.clause).filter(Boolean));
  return ACK_ORDER.cosmetic.filter(
    (key) =>
      !replaced.has(key) &&
      (!COSMETIC_STATE_CLAUSES.includes(key) || named.has(key)) &&
      (!(key in CLAUSES_BY_PROCEDURE) || clauseShownFor(key as CosmeticClauseByProcedure, procedures))
  );
}

/** A COSMETIC TATTOO'S CLAUSES IN ENGLISH, the client's own first person: app.COSMETIC_CONSENT_EN byte for byte, which
 *  the API prints on the signed waiver (its tests read this block and hold the two together), without the full stop the
 *  form adds on screen. The three shared clauses are the tattoo's words, below; they name no service. */
export const COSMETIC_CONSENT_EN: Record<"explained" | CosmeticAckKey, string> = {
  explained: "The artist explained what the cosmetic tattoo involves and I had the chance to ask questions",
  permanentPigment: "I understand this is a tattoo: the color goes into my skin, and removing it can be hard, costly and sometimes impossible",
  darkerAtFirst: "I understand the color will look darker and sharper for the first week or two, then soften as my skin heals",
  fadesAndRefreshes: "I understand the color fades over time, and most people need a perfecting visit about 4 to 8 weeks later (6 or more for lips) and color refreshes over the years",
  resultsVary: "I understand my skin type, health, age, sun exposure and aftercare all change how the color heals and lasts, so no exact result can be promised",
  approvedShapeAndColor: "I saw the shape and the color before the work started, and I approved both",
  pigmentNotApproved: "I understand the FDA has not approved any pigment for injection into the skin",
  normalHealing: "I understand that swelling, redness, tenderness and flaking are normal for a few days, and that infection, scarring or uneven color can happen",
  pigmentAllergy: "I understand an allergic reaction to pigment is rare but can happen, even years later, and a patch test with no reaction does not rule one out",
  numbingReaction: "I told the artist about any reaction I have had to numbing products, and I understand numbing products can also cause a reaction",
  numbingOregon: "I understand that Oregon does not allow my artist to apply numbing medicine, and anything I put on myself is my own choice",
  numbingMichigan: "I understand that Michigan does not allow my artist to work on skin with a numbing product on it unless a doctor prescribed it for this work, and that the prescription is kept with my record",
  answeredTruthfully: "I answered every health question truthfully, and I will tell the artist about any change before my next visit",
  laterTreatments: "I understand that laser, peels, Botox, fillers or surgery in this area can change or darken my cosmetic tattoo, so I will tell the provider it is there",
  mriScan: "If I ever need an MRI, I will tell the technologist about my cosmetic tattoo; it can rarely cause swelling or burning, but that is no reason to skip a scan",
  aftercareGiven: "I got the aftercare instructions for this procedure, spoken and in writing, and I understand that not following them can cause infection or poor results",
  changesWithAge: "I understand my face and body change with age, and my cosmetic tattoo may not look the same years from now",
  lipsColdSore: "I understand lip tattooing can bring on a cold sore, and if I have had them before, I should ask my own doctor about medicine to prevent one",
  eyelinerNearEye: "I understand eyeliner is tattooed right next to my eye and can cause swelling, irritation, lash loss or, in rare cases, an injury to the eye",
  areolaTreatedSkin: "I understand that skin after surgery or radiation can take color unevenly, so more than one session may be needed",
  scalpSessions: "I understand scalp micropigmentation takes several sessions, and the dots can soften, spread or change color over time",
  camouflageMatch: "I understand scar tissue and stretch marks may not hold color the same way as the skin around them, so the match may not be exact",
  microbladingFades: "I understand microblading puts in less pigment than shaded brows, so it may fade sooner, and repeated sessions can leave scar tissue",
};

/** The tattoo clauses that name no service, which a cosmetic form carries in the same words (app.COSMETIC_SHARED_CLAUSES
 *  less the four a cosmetic form does not show; termsAndConditions is the terms box). */
const COSMETIC_SHARED: AckKey[] = ["restrictions", "refund", "indemnify"];

/** The Spanish sentence for a clause — from the one shared file, so it is the sentence the PDF
 *  prints. A piercing's overrides sit on top of the tattoo set, the same shape as the English. A
 *  cosmetic tattoo's is NOT laid over the tattoo set (TW-0279): its own block, and the shared clauses
 *  that name no service, exactly the API's spanish_consent_sentences; a key outside them is the key. */
export function ackLabelEs(key: AckKey | "termsAndConditions", service: ServiceType, shop: string): string {
  const table: Record<string, string> =
    service === "cosmetic"
      ? {
          ...Object.fromEntries([...COSMETIC_SHARED, "termsAndConditions"].map((k) => [k, (consentEs.tattoo as Record<string, string>)[k]])),
          ...consentEs.cosmetic,
        }
      : service === "piercing"
        ? { ...consentEs.tattoo, ...consentEs.piercing }
        : consentEs.tattoo;
  return (table[key] ?? key).split("{shop}").join(shop);
}

/** The tattoo wording is UNCHANGED from what this form has shown since 2023. A cosmetic tattoo's is its own (TW-0279),
 *  and its branch returns before the tattoo's: a clause the cosmetic form does not carry reads as its bare key, never as
 *  a tattoo's or a piercing's sentence. */
export function ackLabel(key: AckKey, service: ServiceType, shop: string, lang: Lang = "en"): string {
  if (lang === "es") return ackLabelEs(key, service, shop);
  if (service === "cosmetic") {
    if (key in COSMETIC_CONSENT_EN) return `${COSMETIC_CONSENT_EN[key as keyof typeof COSMETIC_CONSENT_EN]}.`;
    return COSMETIC_SHARED.includes(key) ? ackLabel(key, "tattoo", shop) : key;
  }
  if (service === "piercing") {
    switch (key) {
      case "explained":
        return "The piercer explained what the piercing involves and I had the chance to ask questions.";
      case "permanentChange":
        return "I understand that a piercing marks my skin and that the mark may not close or fade completely.";
      case "photograph":
        return `I allow my piercing to be photographed and be used for ${shop}'s portfolio showcase.`;
      case "allergic":
        return "I agree that the studio does not have a way of identifying if I am allergic to the metal or materials in the jewelry that will be used for my piercing.";
      case "takeCare":
        return `I understand that I need to take care of the piercing by following the instructions given to me by ${shop}.`;
      case "infection":
        return "I understand that I might get an infection if I don't follow the instructions given to me in regards of taking good care of my piercing.";
      case "migration":
        return "I understand that my body can move or reject a piercing, and that it might not heal the way either of us planned.";
      default:
        break;
    }
  }
  switch (key) {
    case "explained":
      return "The artist explained what the tattoo involves and I had the chance to ask questions.";
    case "outcome":
      return "I understand the result may not come out the way either of us planned, and that a touch-up, correction or removal is not guaranteed.";
    case "restrictions":
      return `I will follow the restrictions ${shop} gives me while it heals, like sun, soaking, picking and anything else they tell me to avoid.`;
    case "permanentChange":
      return "I understand that this procedure is a permanent change to my skin and body.";
    case "photograph":
      return `I allow my tattoo to be photographed and be used for ${shop}'s portfolio showcase.`;
    case "refund":
      return `I acknowledge that ${shop} does not offer refunds.`;
    case "allergic":
      return "I agree that the studio does not have a way of identifying if I am allergic to the elements or ingredients that will be used for my tattoo.";
    case "takeCare":
      return `I understand that I need to take care of the tattoo by following the instructions given to me by ${shop}.`;
    case "infection":
      return "I understand that I might get an infection if I don't follow the instructions given to me in regards of taking good care of my tattoo.";
    case "indemnify":
      return `I indemnify and hold harmless ${shop} against any claims, expenses, damages, and liabilities.`;
    case "information":
      return "I confirm that the information I provided in this document is accurate and true.";
    default:
      return key;
  }
}

/** TW-0279 — what a cosmetic client is told when one of its boxes is left unticked, in the form's own voice ("You must
 *  acknowledge …"). Its own table, returned before the tattoo's and the piercing's logic: a key outside it gets the
 *  generic sentence, never a sentence that names a tattoo or a piercing. */
const COSMETIC_ACK_ERRORS: Record<Lang, Partial<Record<AckKey, (shop: string) => string>>> = {
  en: {
    explained: () => "You must acknowledge that the artist explained the procedure.",
    permanentPigment: () => "You must acknowledge that this is a tattoo, and that removing it can be hard.",
    darkerAtFirst: () => "You must acknowledge that the color looks darker at first.",
    fadesAndRefreshes: () => "You must acknowledge that the color fades and may need a perfecting visit.",
    resultsVary: () => "You must acknowledge that no exact result can be promised.",
    approvedShapeAndColor: () => "You must acknowledge that you saw and approved the shape and the color.",
    pigmentNotApproved: () => "You must acknowledge that the FDA has not approved any pigment for injection.",
    normalHealing: () => "You must acknowledge what is normal while it heals, and what can go wrong.",
    pigmentAllergy: () => "You must acknowledge that an allergic reaction to pigment can happen.",
    numbingReaction: () => "You must acknowledge what you told the artist about numbing products.",
    numbingOregon: () => "You must acknowledge what Oregon's rule says about numbing medicine.",
    numbingMichigan: () => "You must acknowledge what Michigan's rule says about numbing products.",
    answeredTruthfully: () => "You must acknowledge that you answered every health question truthfully.",
    laterTreatments: () => "You must acknowledge that later treatments in this area can change your cosmetic tattoo.",
    mriScan: () => "You must acknowledge what you will tell a technologist before an MRI.",
    aftercareGiven: () => "You must acknowledge that you got the aftercare instructions.",
    changesWithAge: () => "You must acknowledge that your cosmetic tattoo may look different as you age.",
    lipsColdSore: () => "You must acknowledge that lip tattooing can bring on a cold sore.",
    eyelinerNearEye: () => "You must acknowledge the risks of eyeliner next to your eye.",
    areolaTreatedSkin: () => "You must acknowledge that treated skin can take color unevenly.",
    scalpSessions: () => "You must acknowledge that scalp micropigmentation takes several sessions.",
    camouflageMatch: () => "You must acknowledge that the color match may not be exact.",
    microbladingFades: () => "You must acknowledge that microblading may fade sooner.",
    restrictions: () => "You must acknowledge the restrictions while it heals.",
    refund: () => "You must acknowledge that we do not offer refunds.",
    indemnify: (shop) => `You must acknowledge that you indemnify and hold harmless ${shop} and its employees.`,
  },
  // awaiting a native-speaker read, like every Spanish line on the form
  es: {
    explained: () => "Debes reconocer que te explicaron el procedimiento.",
    permanentPigment: () => "Debes reconocer que esto es un tatuaje, y que quitarlo puede ser difícil.",
    darkerAtFirst: () => "Debes reconocer que al principio el color se ve más oscuro.",
    fadesAndRefreshes: () => "Debes reconocer que el color se desvanece y puede necesitar una visita de perfeccionamiento.",
    resultsVary: () => "Debes reconocer que no se puede prometer un resultado exacto.",
    approvedShapeAndColor: () => "Debes reconocer que viste y aprobaste la forma y el color.",
    pigmentNotApproved: () => "Debes reconocer que la FDA no ha aprobado ningún pigmento para inyectarse.",
    normalHealing: () => "Debes reconocer lo que es normal mientras sana, y lo que puede salir mal.",
    pigmentAllergy: () => "Debes reconocer que puede haber una reacción alérgica al pigmento.",
    numbingReaction: () => "Debes reconocer lo que le dijiste al artista sobre los productos para adormecer la piel.",
    numbingOregon: () => "Debes reconocer lo que dice la regla de Oregon sobre los medicamentos para adormecer la piel.",
    numbingMichigan: () => "Debes reconocer lo que dice la regla de Míchigan sobre los productos para adormecer la piel.",
    answeredTruthfully: () => "Debes reconocer que respondiste con la verdad todas las preguntas de salud.",
    laterTreatments: () => "Debes reconocer que otros tratamientos en esta zona pueden cambiar tu tatuaje cosmético.",
    mriScan: () => "Debes reconocer lo que le dirás al técnico antes de una resonancia magnética.",
    aftercareGiven: () => "Debes reconocer que recibiste las instrucciones de cuidado.",
    changesWithAge: () => "Debes reconocer que tu tatuaje cosmético puede verse distinto con los años.",
    lipsColdSore: () => "Debes reconocer que tatuar los labios puede provocar un herpes labial.",
    eyelinerNearEye: () => "Debes reconocer los riesgos del delineado junto a tu ojo.",
    areolaTreatedSkin: () => "Debes reconocer que la piel tratada puede tomar el color de forma dispareja.",
    scalpSessions: () => "Debes reconocer que la micropigmentación del cuero cabelludo lleva varias sesiones.",
    camouflageMatch: () => "Debes reconocer que el tono puede no quedar idéntico.",
    microbladingFades: () => "Debes reconocer que el microblading puede desvanecerse antes.",
    restrictions: () => "Debes aceptar las restricciones mientras sana.",
    refund: () => "Debes reconocer que no ofrecemos reembolsos.",
    indemnify: (shop) => `Debes reconocer que eximes de responsabilidad a ${shop} y a su personal.`,
  },
};

function ackErrorCosmetic(key: AckKey, shop: string, lang: Lang): string {
  const words = COSMETIC_ACK_ERRORS[lang][key];
  if (words) return words(shop);
  return lang === "es" ? "Debes marcar todas las casillas." : "You must acknowledge every item.";
}

/** The sentence shown when a box is left unticked — the same voice the form has always used. */
export function ackError(key: AckKey, service: ServiceType, shop: string, lang: Lang = "en"): string {
  if (service === "cosmetic") return ackErrorCosmetic(key, shop, lang);
  if (lang === "es") return ackErrorEs(key, service, shop);
  const thing = service === "piercing" ? "piercing" : "tattoo";
  switch (key) {
    case "permanentChange":
      return service === "piercing"
        ? "You must acknowledge that a piercing marks your skin."
        : "You must acknowledge that this is a permanent change.";
    case "photograph":
      return `You must acknowledge that we can photograph your ${thing}.`;
    case "refund":
      return "You must acknowledge that we do not offer refunds.";
    case "allergic":
      return "You must acknowledge that we cannot identify if you are allergic.";
    case "takeCare":
      return `You must acknowledge that you are responsible for taking care of your ${thing}.`;
    case "infection":
      return `You must acknowledge that you can get an infection if you do not take care of your ${thing}.`;
    case "migration":
      return "You must acknowledge that a piercing can migrate or be rejected.";
    case "indemnify":
      return `You must acknowledge that you indemnify and hold harmless ${shop} and its employees.`;
    case "information":
      return "You must acknowledge that the information you provided is accurate and true.";
    case "explained":
      return `You must acknowledge that the ${service === "piercing" ? "piercer" : "artist"} explained the procedure.`;
    case "outcome":
      return "You must acknowledge that the result is not guaranteed.";
    case "restrictions":
      return "You must acknowledge the restrictions while it heals.";
    default:
      return "You must acknowledge every item.";
  }
}

function ackErrorEs(key: AckKey, service: ServiceType, shop: string): string {
  const thing = service === "piercing" ? "perforación" : "tatuaje";
  switch (key) {
    case "permanentChange":
      return service === "piercing"
        ? "Debes reconocer que una perforación deja una marca en tu piel."
        : "Debes reconocer que este es un cambio permanente.";
    case "photograph":
      return `Debes aceptar que podemos fotografiar tu ${thing}.`;
    case "refund":
      return "Debes reconocer que no ofrecemos reembolsos.";
    case "allergic":
      return "Debes reconocer que no podemos saber si eres alérgico/a.";
    case "takeCare":
      return `Debes reconocer que el cuidado de tu ${thing} es tu responsabilidad.`;
    case "infection":
      return `Debes reconocer que puedes contraer una infección si no cuidas tu ${thing}.`;
    case "migration":
      return "Debes reconocer que una perforación puede desplazarse o ser rechazada.";
    case "indemnify":
      return `Debes reconocer que eximes de responsabilidad a ${shop} y a su personal.`;
    case "information":
      return "Debes reconocer que la información que proporcionaste es exacta y verdadera.";
    case "explained":
      return "Debes reconocer que te explicaron el procedimiento.";
    case "outcome":
      return "Debes reconocer que el resultado no está garantizado.";
    case "restrictions":
      return "Debes aceptar las restricciones mientras sana.";
    default:
      return "Debes marcar todas las casillas.";
  }
}

// ── what gets posted ───────────────────────────────────────────────────────────────────────────

/** The piercing-only fields on the form's state, and the piercing-only questionnaire keys. */
export const PIERCING_ONLY_FIELDS = [
  "jewelryMaterial",
  "jewelryNotes",
  "metalAllergyDetails",
  "keloidScarringDetails",
  "recentDentalWorkDetails",
] as const;

export const PIERCING_ONLY_QUESTIONS = ["metalAllergy", "keloidScarring", "recentDentalWork"] as const;

/** TW-0279 — a cosmetic tattoo's own fields on the form's state: the procedures, what the visit is, and the box under
 *  each of its own questions (keloidScarringDetails is the piercing's too, so it is in both lists). TW-0279 slice 10 — and
 *  whether a scalp signing covers scars, which leaves EVERY body here, a cosmetic one too: Form.tsx puts it back, as a
 *  boolean, only where the question was asked (cosmetic.ts scalpNamed). */
export const COSMETIC_ONLY_FIELDS: readonly string[] = [
  "cosmeticProcedures",
  "cosmeticVisit",
  "scalpCoversScars",
  ...COSMETIC_QUESTIONS.flatMap((q) => (q.details ? [q.details as string] : [])),
];

export const COSMETIC_ONLY_QUESTIONS: readonly string[] = COSMETIC_QUESTIONS.map((q) => q.name as string);

/** Every question only some services ask, and every field only some services post: the denominators of the strips in
 *  submissionForm. A key on the form's state that no service may post rides EVERY body unless it is in one of these. */
export const SERVICE_ONLY_QUESTIONS: readonly string[] = Array.from(new Set([...PIERCING_ONLY_QUESTIONS, ...COSMETIC_ONLY_QUESTIONS]));
export const SERVICE_ONLY_FIELDS: readonly string[] = Array.from(new Set([...PIERCING_ONLY_FIELDS, ...COSMETIC_ONLY_FIELDS]));

/** What submissionForm needs to know about the shop beyond its type: its `public_data.cosmetic`, and for a cosmetic
 *  signing its state (whose own lines decide the clauses, ackOrderFor) and the procedures its picker offered (cosmetic.ts
 *  cosmeticProceduresOffered: every one but those its state bars). Absent, it is a shop with no cosmetic key and no
 *  state, whose picker would offer everything. */
export type SubmissionShop = {
  cosmetic?: unknown;
  state?: string | null;
  offered?: readonly string[];
  /** 2026-10-08 — the shop is in HIPAA mode (hipaaForm.ts): its body never carries the ordinary portfolio clause */
  hipaa?: boolean;
  /** TW-0279 slice 9 — the shop's row (`public_data`), whose own switch can take a state's own question off a cosmetic
   *  page (cosmetic.ts cosmeticQuestionsFor: South Carolina's face question at a physician-directed studio) */
  publicData?: unknown;
};

/** THE BODY THE FORM POSTS, for the service the client is actually here for.
 *
 *  A tattoo-only shop posts EXACTLY what it has posted since 2023: no `serviceType`, no jewelry,
 *  no piercing questions, no `migration` acknowledgement. That is not a nicety — every one of the
 *  six has thousands of printed QR codes pointing at it, and its record is the shop's legal one.
 *
 *  A shop that offers more than one service names the service, because "which of the two was this"
 *  is a real question about that row. A piercing carries its jewelry, the questions it was asked
 *  (and only those: an earlobe's record never says anything about dental work), and the one extra
 *  acknowledgement.
 *
 *  A COSMETIC TATTOO (TW-0279) ALWAYS NAMES ITSELF, at every shop, a cosmetic studio included: an absent `serviceType`
 *  is a tattoo everywhere, for good (docs/COSMETIC.md's second invariant), so a cosmetic body that said nothing would be
 *  signed as a tattoo's. It carries its procedures (only ones its picker offered, in the list's order: a procedure the
 *  shop's state bars is never posted, whatever the state of the page), what the visit is, its own questions (those of
 *  every cosmetic form and those its procedures bring, never one it did not ask) with their boxes, and its own clauses
 *  in the order the form shows them (only those shown: a brow's record never says anything about lips), then the boxes
 *  every service has. No clause the cosmetic form does not carry is ever on its body (the review's F2). */
export function submissionForm<T extends Record<string, any>>(
  form: T,
  type: ShopType,
  service: ServiceType,
  location: string,
  shop: SubmissionShop = {},
): Record<string, any> {
  const out: Record<string, any> = { ...form };
  const questionnaire: Record<string, any> = { ...(form.questionnaire ?? {}) };
  const acks: Record<string, any> = { ...(form.acknowledgmentAndWaiver ?? {}) };
  const procedures = service === "cosmetic" ? postedProcedures(form.cosmeticProcedures, shop.offered ?? COSMETIC_PROCEDURE_VALUES) : [];

  // the questions only some services ask, kept only where this signing asked them, with the box each one opens
  // (a cosmetic tattoo's state question, New Jersey's pregnancy bar, California's herpes question or the face question,
  // only where the shop's state's record names it and the shop's own switch has not taken it off the page)
  const own: { name: string; details?: string }[] =
    service === "piercing"
      ? piercingQuestionsFor(location)
      : service === "cosmetic"
        ? cosmeticQuestionsFor(procedures, shop.state ?? null, shop.publicData)
        : [];
  const asked = new Set(own.map((q) => q.name));
  for (const key of SERVICE_ONLY_QUESTIONS) if (!asked.has(key)) delete questionnaire[key];

  const kept = new Set<string>([
    ...own.flatMap((q) => (q.details ? [q.details] : [])),
    ...(service === "piercing" ? ["jewelryMaterial", "jewelryNotes"] : []),
    ...(service === "cosmetic" ? ["cosmeticProcedures", "cosmeticVisit"] : []),
  ]);
  for (const field of SERVICE_ONLY_FIELDS) if (!kept.has(field)) delete out[field];
  if (service === "cosmetic") {
    out.cosmeticProcedures = procedures;
    out.cosmeticVisit = `${form.cosmeticVisit ?? ""}`;
  }

  if (service === "cosmetic") {
    // its own clauses in the order shown, as booleans, then every box that is not a service clause (the terms, the two
    // optional ones) where the form's state had it
    const shown = ackOrderFor("cosmetic", procedures, shop.state ?? null);
    const rebuilt: Record<string, any> = {};
    for (const key of shown) rebuilt[key] = acks[key] === true;
    for (const [key, value] of Object.entries(acks)) {
      if (!(ALL_ACK_KEYS as string[]).includes(key)) rebuilt[key] = value;
    }
    out.acknowledgmentAndWaiver = rebuilt;
  } else {
    for (const key of ALL_ACK_KEYS) if (!ACK_ORDER[service].includes(key)) delete acks[key];
    // 2026-10-08 — a shop in HIPAA mode: no portfolio clause on its record (hipaaForm.ts PORTFOLIO_CLAUSE; the API takes it
    // off too). Every other shop's body is the one it has always posted.
    if (shop.hipaa === true) delete acks.photograph;
    out.acknowledgmentAndWaiver = acks;
  }

  out.questionnaire = questionnaire;
  // A shop whose form can produce only a tattoo posts no `serviceType`, as it never has; every other shop names the
  // service, and a cosmetic tattoo is named at every shop.
  const services = servicesFor(type, shop.cosmetic);
  if (service !== "cosmetic" && services.length === 1 && services[0] === "tattoo") delete out.serviceType;
  else out.serviceType = service;
  return out;
}
